import { Controller } from "@hotwired/stimulus"

// Six segmented boxes backed by one real input.
//
// The boxes are decoration; the input underneath is a single transparent field
// covering them. That matters: iOS one-time-code autofill and paste both target
// a single field and break on six separate inputs.
//
// When autoSubmit is on the form sends itself at six digits, so the common case
// is scan the code, tap nothing. The fallback submit button is only hidden once
// this controller connects, so a browser without JS still has a way to submit.
export default class extends Controller {
  static targets = ["input", "box", "status", "fallback"]
  // Off for destructive actions — nobody should turn their second factor off by
  // finishing a word.
  static values = { autoSubmit: { type: Boolean, default: true } }

  connect() {
    // Reset explicitly rather than relying on a fresh instance. The layout sets
    // turbo-refresh-method=morph, so this element can survive a re-render and
    // arrive here with stale state — which previously left `busy` true after a
    // rejected code and made typing do nothing until the field lost focus.
    this.busy = false

    if (this.autoSubmitValue && this.hasFallbackTarget) {
      this.fallbackTarget.classList.add("hidden")
    }

    // Clearing the shake class once it finishes lets it replay on the next miss.
    this.clearRejected = () => this.element.classList.remove("otp--rejected")
    this.element.addEventListener("animationend", this.clearRejected)

    // Whatever the outcome — rejected, network error, morph — the field has to
    // become usable again. Without this the busy flag is a one-way door.
    this.form = this.element.closest("form")
    this.releaseBusy = () => this.reset()
    this.form?.addEventListener("turbo:submit-end", this.releaseBusy)

    this.render()
  }

  disconnect() {
    this.element.removeEventListener("animationend", this.clearRejected)
    this.form?.removeEventListener("turbo:submit-end", this.releaseBusy)
  }

  render() {
    const value = this.inputTarget.value.replace(/\D/g, "").slice(0, this.boxTargets.length)
    const focused = document.activeElement === this.inputTarget

    this.boxTargets.forEach((box, i) => {
      box.textContent = value[i] || ""
      box.classList.toggle("otp-box--filled", Boolean(value[i]))
      box.classList.toggle("otp-box--active", focused && i === value.length && !this.busy)
    })
  }

  input() {
    if (this.busy) return

    // Strip anything non-numeric so a pasted "482 913" still lands correctly.
    const digits = this.inputTarget.value.replace(/\D/g, "").slice(0, this.boxTargets.length)
    if (digits !== this.inputTarget.value) this.inputTarget.value = digits

    this.render()

    if (this.autoSubmitValue && digits.length === this.boxTargets.length) {
      this.submit()
    }
  }

  submit() {
    if (this.busy || !this.form) return

    // Visible acknowledgement. Without it the sixth digit lands and nothing
    // appears to happen until the next page paints.
    this.busy = true
    this.element.classList.remove("otp--rejected")
    this.element.classList.add("otp--busy")
    this.inputTarget.blur()
    if (this.hasStatusTarget) this.statusTarget.classList.remove("hidden")
    this.render()

    this.form.requestSubmit()
  }

  // Back to a usable, empty field. A rejected code should never be left in
  // place for the user to edit around.
  reset() {
    this.busy = false
    this.element.classList.remove("otp--busy")
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden")
    this.inputTarget.value = ""
    this.render()
  }

  focus() {
    this.render()
  }

  blur() {
    this.render()
  }

  // Tapping anywhere on the boxes should put the caret in the real field.
  select() {
    if (!this.busy) this.inputTarget.focus()
  }
}
